import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "@/shared/utils";
import { useGetCitiesQuery, useGetRegionsQuery } from "@/shared/api/common";
import type { CityResponse, RegionResponse } from "@/shared/api/common";
import SearchableSelect from "../SearchableSelect";
import { Input } from "../Input";

interface CitySelectProps {
  label?: string;
  hasRequiredLabel?: boolean;
  error?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  value?: number | undefined; 
  onChange?: (cityId: number | undefined) => void;
  width?: string;
  disabled?: boolean;
  withOther?: boolean;
  otherLabel?: string;
  otherPlaceholder?: string;
  otherValue?: string;
  otherError?: string;
  onOtherChange?: (value: string) => void;
  dataSource?: "cities" | "regions";
}

type SelectableOption = CityResponse | RegionResponse;

export default function CitySelect({
  label,
  hasRequiredLabel = false,
  error,
  placeholder,
  searchPlaceholder,
  value,
  onChange,
  width = "w-full",
  disabled = false,
  withOther = false,
  otherLabel = "",
  otherPlaceholder,
  otherValue,
  otherError,
  onOtherChange,
  dataSource = "cities",
}: CitySelectProps) {
  const { i18n, t } = useTranslation("Common");
  const locale = i18n.language as Locale;
  const { data: citiesData = [], isLoading: isLoadingCities } = useGetCitiesQuery(undefined, {
    skip: dataSource === "regions",
  });
  const { data: regionsData = [], isLoading: isLoadingRegions } = useGetRegionsQuery(undefined, {
    skip: dataSource === "cities",
  });

  const data = dataSource === "regions" ? regionsData : citiesData;
  const isLoading = dataSource === "regions" ? isLoadingRegions : isLoadingCities;

  type Mode = "city" | "other" | null;
  const [mode, setMode] = useState<Mode>(null);

  type OtherOption = {
    id: -1;
    __isOther: true;
    name_ru: string;
    name_kk: string;
    region_ru?: string;
    region_kk?: string;
  };

  const otherOption: OtherOption = useMemo(() => {
    const label = t("other");
    return {
      id: -1,
      __isOther: true,
      name_ru: label,
      name_kk: label,
      region_ru: "",
      region_kk: "",
    };
  }, [t]);

  function isOtherOption(opt: SelectableOption | OtherOption): opt is OtherOption {
    return "__isOther" in opt && opt.__isOther === true;
  }

  const selectedItem = useMemo(() => {
    if (!value || data.length === 0) return null;
    return data.find((item) => item.id === value) || null;
  }, [value, data]);

  const normalizedOtherValue = otherValue ?? "";

  useEffect(() => {
    if (!withOther) {
      setMode("city");
      return;
    }

    if (value !== undefined) {
      setMode("city");
      return;
    }

    if (normalizedOtherValue.trim() !== "") {
      setMode("other");
      return;
    }

    setMode((prev) => (prev === "other" ? prev : null));
  }, [withOther, value, normalizedOtherValue]);

  function getDisplayName(item: SelectableOption): string {
    return locale === "kk" ? item.name_kk : item.name_ru;
  }

  const loadingPlaceholder = isLoading ? t("loadingCities") : placeholder;

  const options = useMemo(() => {
    return withOther ? ([...data, otherOption] as Array<SelectableOption | OtherOption>) : data;
  }, [data, withOther, otherOption]);

  const selectedOption = useMemo(() => {
    if (!withOther) return selectedItem;
    if (mode === "other") return otherOption;
    return selectedItem;
  }, [withOther, mode, otherOption, selectedItem]);

  const searchKeys = useMemo(() => {
    return ["name_ru", "name_kk"] as const;
  }, []) as (keyof SelectableOption)[];

  return (
    <div className="flex flex-col gap-3">
      <SearchableSelect<SelectableOption | OtherOption>
        label={label}
        hasRequiredLabel={hasRequiredLabel}
        error={withOther && mode === "other" ? undefined : error}
        placeholder={loadingPlaceholder}
        searchPlaceholder={searchPlaceholder}
        options={options}
        value={selectedOption}
        onChange={(opt) => {
          if (!opt) return;

          if (isOtherOption(opt)) {
            setMode("other");
            onChange?.(undefined);
            return;
          }

          setMode("city");
          onChange?.(opt.id ?? undefined);
          onOtherChange?.("");
        }}
        searchKeys={searchKeys as (keyof (SelectableOption | OtherOption))[]}
        displayKey="name_ru"
        getOptionLabel={(opt) => {
          if (isOtherOption(opt)) return t("other");
          return getDisplayName(opt);
        }}
        isOptionEqual={(a, b) => {
          if (isOtherOption(a) && isOtherOption(b)) return true;
          if (!isOtherOption(a) && !isOtherOption(b)) return a.id === b.id;
          return false;
        }}
        width={width}
        disabled={disabled || isLoading}
      />

      {withOther && mode === "other" && (
        <Input
          label={otherLabel}
          placeholder={otherPlaceholder ?? t("other")}
          value={normalizedOtherValue}
          onChange={(e) => onOtherChange?.(e.target.value)}
          error={otherError ?? error}
          disabled={disabled}
        />
      )}
    </div>
  );
}

